import { MobileSortDrawer } from '@components/MobileSortDrawer/MobileSortDrawer';
import { SortButton } from '@components/SortSelect/SortButton';
import { useDebouncedValue } from '@lib/hooks/debounce';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { formatKey, KEY_SHORTCUTS } from '@lib/keyboard/shortcuts';
import { draftAnchor, formatAnchorLabel, type MediaCommentAnchor } from '@lib/media/commentAnchor';
import { authorLabel } from '@lib/media/commentAuthor';
import {
  availableCommentSortGroups,
  COMMENT_SORT_LABELS,
  effectiveCommentSort,
  groupByAuthor,
  sortComments,
  type CommentSort,
} from '@lib/media/commentSort';
import { showToast, type MediaCommentAction } from '@lib/utils/toastmaster';
import { formatClockLabel, pluralize } from '@lib/utils/units';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import type { MediaComment, MediaCommentsStore } from '@stores/MediaComments';
import { userStore } from '@stores/User';
import { VideoPlayerStore } from '@stores/VideoPlayer';
import { observer } from 'mobx-react';
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { CommentComposer } from './CommentComposer';
import { CommentThread, threadSelector, type ThreadActions } from './CommentThread';
import css from './CommentsPanel.module.scss';

/**
 * How far the playhead must have travelled from a pinned frame anchor before the composer
 * offers to move it. Under a second the offer would flicker on and off while the media plays,
 * for a move nobody would notice having made.
 */
const REPIN_OFFER_THRESHOLD_SECONDS = 1;

/**
 * How often the composer's anchor line re-reads the playhead. `currentTime` updates as fast as
 * the video element reports; the line only has to keep up with a reader's eye, and four times a
 * second is past the point anyone can tell.
 */
const PLAYHEAD_POLL_MS = 250;

type Props = {
  store: MediaCommentsStore;
  playerStore: VideoPlayerStore;
  /**
   * False in a read-only library, where the API refuses every write. The list is still shown —
   * reading is allowed — so the panel says why the composer is missing rather than looking
   * broken.
   */
  canWrite: boolean;
  /**
   * The thread last gone to — from its own timestamp chip, or from a marker on the scrub bar or
   * the filmstrip. Owned by the modal because the markers are outside this panel.
   */
  selectedCommentId: string | null;
  /** A comment's timestamp chip was pressed. The modal's `goToComment`, shared with the markers. */
  onGoToComment: (comment: MediaComment) => void;
  /** The selection bar's Comment button asked for the composer. See `CommentComposer.focusRequested`. */
  focusComposer: boolean;
  onComposerFocused: () => void;
  /**
   * The list's order. Held by the modal, not here, because this panel unmounts whenever another
   * tab is chosen — a sort kept in here would quietly reset every time the reader glanced at the
   * transcript and came back.
   */
  sort: CommentSort;
  onSortChange: (sort: CommentSort) => void;
  /** A comment's "Copy link". The modal builds the link — it holds the media and its library. */
  onCopyLink: (comment: MediaComment) => void;
};

/**
 * Notes left on this media, each pointing at a frame or a span of it.
 *
 * The list is in timeline order rather than the creation order the API returns (see
 * `compareByAnchor`): it is read while scrubbing, beside a scrub bar and a filmstrip that are
 * both in that order too.
 *
 * Nothing here scrolls the list as playback moves. A transcript follows along because there is
 * a line for every second; comments are sparse, so following would mean the list sitting still
 * for a minute and then jumping — and it would fight the one scroll the reader did ask for,
 * which is a marker click landing on its thread.
 */
export const CommentsPanel = observer(
  ({
    store,
    playerStore,
    canWrite,
    selectedCommentId,
    onGoToComment,
    focusComposer,
    onComposerFocused,
    sort,
    onSortChange,
    onCopyLink,
  }: Props) => {
    const listRef = useRef<HTMLDivElement>(null);
    const comments = store.data;
    const currentUserId = userStore.data?._id ?? null;
    const sortGroups = availableCommentSortGroups(comments ?? []);
    const activeSort = effectiveCommentSort(sort, sortGroups);

    /**
     * Runs a write, telling the reader when it fails, and says whether it landed.
     *
     * All six writes want exactly this, and the store deliberately does not do it — it owns the
     * facts, not what the reader is told about them. A boolean rather than a rethrow because by
     * this point the failure has been handled: rethrowing would leave four call sites catching
     * an error purely to ignore it.
     */
    const report = async (action: MediaCommentAction, write: Promise<unknown>) => {
      try {
        await write;
        return true;
      } catch (error) {
        showToast('media-comment-failed', { action, error });
        return false;
      }
    };

    const actions: ThreadActions = {
      edit: (commentId, body) => report('edit', store.edit(commentId, { body })),
      remove: (commentId) => report('delete', store.remove(commentId)),
      reply: (commentId, body) => report('reply', store.reply(commentId, body)),
      editReply: (commentId, replyId, body) =>
        report('edit-reply', store.editReply(commentId, replyId, body)),
      removeReply: (commentId, replyId) =>
        report('delete-reply', store.removeReply(commentId, replyId)),
    };

    // Brings the thread just gone to into view. `nearest`, so a thread already on screen
    // is left where it is rather than yanked to the middle — clicking a marker for a note you
    // can already read should change nothing but the highlight.
    useLayoutEffect(() => {
      if (!selectedCommentId) return;
      listRef.current
        ?.querySelector(threadSelector(selectedCommentId))
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [selectedCommentId, comments]);

    return (
      <div className={css.panel}>
        {canWrite ? (
          <NewCommentComposer
            playerStore={playerStore}
            onPost={(body, anchor) => report('post', store.create(body, anchor))}
            focusRequested={focusComposer}
            onFocusRequestHandled={onComposerFocused}
          />
        ) : (
          <p className={css.readOnlyNotice}>
            This library is read-only, so its media can&rsquo;t be commented on.
          </p>
        )}

        {/* The list's own header, sitting on it: what it holds on the left, how it is ordered on
            the right. Outside the scroller, so it stays put as the list scrolls under it.

            The sort only once there is something to order — with one comment every sort is the
            same list, and a control that changes nothing is clutter. */}
        {comments && comments.length > 0 && (
          <div className={css.listHeader}>
            <span className={css.listCount}>{pluralize(comments.length, 'comment')}</span>
            {comments.length > 1 && (
              <SortControl value={activeSort} groups={sortGroups} onChange={onSortChange} />
            )}
          </div>
        )}

        <div ref={listRef} className={css.list}>
          {/* The three states are kept apart on purpose. `data` is null both before the first
              read and after a failed one, so "No comments yet" — a claim about the media —
              is only ever made when a read actually came back. */}
          {store.error && !comments ? (
            <Placeholder headline="Couldn't load comments">
              Check your connection and reopen this media.
            </Placeholder>
          ) : !comments ? (
            // In flight, or not asked yet — two states that look the same through `data` alone
            // and must not read the same on screen. Only the first says anything.
            store.fetching ? (
              <Placeholder headline="Loading comments…" />
            ) : null
          ) : comments.length === 0 ? (
            <Placeholder headline="No comments yet">
              {canWrite
                ? 'Notes you leave are pinned to wherever you are in the media.'
                : 'Nobody has left a note on this media.'}
            </Placeholder>
          ) : (
            (() => {
              const sorted = sortComments(comments, activeSort, currentUserId);
              const renderThread = (comment: MediaComment) => (
                <CommentThread
                  key={comment.id}
                  comment={comment}
                  currentUserId={currentUserId}
                  canWrite={canWrite}
                  isSelected={comment.id === selectedCommentId}
                  onGoTo={() => onGoToComment(comment)}
                  onCopyLink={() => onCopyLink(comment)}
                  actions={actions}
                />
              );
              // Sorted by commenter, each person's comments sit under their name. Without the
              // headings, grouping by person looks like any other order.
              if (activeSort !== 'commenter') return sorted.map(renderThread);
              return groupByAuthor(sorted).map((section) => (
                <Fragment key={section.userId}>
                  <h3 className={css.sectionHeading}>
                    {authorLabel(section.items[0].author, currentUserId)}
                    <span className={css.sectionCount}>{section.items.length}</span>
                  </h3>
                  {section.items.map(renderThread)}
                </Fragment>
              ));
            })()
          )}
        </div>
      </div>
    );
  },
);

/**
 * The list's sort, as the shared `SortButton` face on both layouts: its own menu on desktop, and
 * on a phone the `MobileSortDrawer` sheet every other sort picker there uses. Both are fed the
 * same `groups`, so the two can never offer different options.
 */
const SortControl = ({
  value,
  groups,
  onChange,
}: {
  value: CommentSort;
  /** What this list offers — see `availableCommentSortGroups`. */
  groups: { value: CommentSort; label: string }[][];
  onChange: (sort: CommentSort) => void;
}) => {
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <>
      <SortButton
        variant="text"
        selectedLabel={COMMENT_SORT_LABELS[value]}
        value={value}
        options={groups}
        onChange={(next) => onChange(next as CommentSort)}
        tooltip="Sort comments by"
        onRequestOpen={isMobile ? () => setDrawerOpen(true) : undefined}
      />
      {isMobile && (
        <MobileSortDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          groups={groups.map((group) => group.map((option) => ({ key: option.value, ...option })))}
          selectedKey={value}
          onSelect={onChange}
        />
      )}
    </>
  );
};

const Placeholder = ({ headline, children }: { headline: string; children?: ReactNode }) => (
  <div className={css.placeholder}>
    <ChatBubbleOutlineRoundedIcon className={css.placeholderIcon} />
    <span className={css.placeholderHeadline}>{headline}</span>
    {children && <span className={css.placeholderBody}>{children}</span>}
  </div>
);

/**
 * The composer, and the line above it saying where what you write will point.
 *
 * The anchor follows the playhead until the field is focused, then holds still. Freezing it is
 * the whole point: the media keeps playing while a note is typed, so an anchor that kept
 * following would file the note wherever playback happened to reach by the time it was sent —
 * which is never where the writer was looking when they started.
 *
 * Marking a range un-freezes it, because dragging out a span mid-sentence is an unambiguous
 * statement about where the note belongs, and because a moment opened from search arrives with
 * its bounds already marked.
 */
const NewCommentComposer = observer(
  ({
    playerStore,
    onPost,
    focusRequested,
    onFocusRequestHandled,
  }: {
    playerStore: VideoPlayerStore;
    onPost: (body: string, anchor: MediaCommentAnchor) => Promise<boolean>;
    focusRequested: boolean;
    onFocusRequestHandled: () => void;
  }) => {
    const [pinned, setPinned] = useState<MediaCommentAnchor | null>(null);
    const currentTime = useDebouncedValue(playerStore.currentTime, PLAYHEAD_POLL_MS);
    const selection = playerStore.moment;
    const selectionStart = selection?.start;
    const selectionEnd = selection?.end;
    /** A range the API would actually accept — the same test `draftAnchor` applies. */
    const hasSelection = !!selection && selection.end > selection.start;

    const anchor = pinned ?? draftAnchor(selection, currentTime);

    // Keyed on the bounds rather than on the object: `setMoment` is called with a fresh object
    // on every frame of a scrub-bar drag, and un-freezing on each of those would put the anchor
    // back under the playhead mid-drag.
    useEffect(() => setPinned(null), [selectionStart, selectionEnd]);

    // Read live, not from the debounced copy: this is the instant the writer acted, and up to a
    // quarter of a second of staleness is a visibly wrong number on a paused frame.
    const pinHere = () => setPinned(draftAnchor(playerStore.moment, playerStore.currentTime));

    // Focus only pins when nothing is pinned yet. Clicking the video to pause and coming back to
    // finish a sentence is a second focus, and re-pinning on it would quietly move a half-written
    // note to wherever playback had got to.
    const pinIfUnpinned = () => {
      if (!pinned) pinHere();
    };

    /**
     * The one control beside the anchor line, and which of three offers it makes.
     *
     * Exactly one at a time, because they are the only three ways the anchor can be wrong:
     * it adopted a range the writer did not mean, it is on a frame when the range was what
     * they meant, or it is on a frame they have since scrolled away from. Anything the anchor
     * already says is not offered — a button that changes nothing is worse than no button.
     */
    const override = (() => {
      if (anchor.type === 'range') {
        return {
          label: `Use ${formatClockLabel(playerStore.currentTime)}`,
          // Not `pinHere`: that adopts the selection, which is the thing being overridden.
          apply: () =>
            setPinned({ type: 'frame', timestamp: Math.max(0, playerStore.currentTime) }),
        };
      }
      if (hasSelection) {
        // Back to following, which with a range marked resolves to that range.
        return { label: 'Use selection', apply: () => setPinned(null) };
      }
      if (
        pinned &&
        Math.abs(playerStore.currentTime - anchor.timestamp) > REPIN_OFFER_THRESHOLD_SECONDS
      ) {
        return { label: `Move to ${formatClockLabel(playerStore.currentTime)}`, apply: pinHere };
      }
      return null;
    })();

    return (
      <CommentComposer
        placeholder="Leave a note on this media…"
        submitLabel="Comment"
        onFocus={pinIfUnpinned}
        keyShortcut={formatKey(KEY_SHORTCUTS.startComment)}
        focusRequested={focusRequested}
        onFocusRequestHandled={() => {
          // Re-pinned outright rather than left to `pinIfUnpinned`: the button says "Comment on
          // the selection", and that is the most recent thing the reader asked for — it has to
          // beat an earlier "Use 1:24" that pinned the composer to a frame instead.
          pinHere();
          onFocusRequestHandled();
        }}
        onSubmit={async (body) => {
          const posted = await onPost(body, anchor);
          // Back to following the playhead, but only once the note is actually filed — a
          // refused post keeps both the text and the spot it was written about.
          if (posted) setPinned(null);
          return posted;
        }}
        header={
          <div className={css.draftAnchor}>
            <AccessTimeRoundedIcon className={css.draftAnchorIcon} />
            <span className={css.draftAnchorLabel}>{formatAnchorLabel(anchor)}</span>
            <span className={css.draftAnchorHint}>
              {anchor.type === 'range' ? 'your selection' : 'this frame'}
            </span>
            {override && (
              <button type="button" className={css.linkButton} onClick={override.apply}>
                {override.label}
              </button>
            )}
            {/* Where a shortcut is learned: on the thing it opens, seen every time the tab is
                open, so the next comment can start with a key instead of a trip here. */}
            <kbd className={css.shortcutHint} title="Press C while watching to start a comment">
              {formatKey(KEY_SHORTCUTS.startComment)}
            </kbd>
          </div>
        }
      />
    );
  },
);
